import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Save, RotateCcw, ChevronDown, ChevronUp } from "lucide-react";
import { defaultTranslations } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LANGS = ["fr", "en", "es", "pt"];

const GROUPS = [
  { id: "hero", label: "Accueil — Hero", keys: ["hero.eyebrow", "hero.l1", "hero.l2", "hero.l3a", "hero.l3b", "hero.quote", "hero.cta1", "hero.cta2"] },
  { id: "event", label: "Bannière Événement", keys: ["event.eyebrow", "event.quote", "event.meta.location", "event.meta.date", "event.meta.ages", "event.text", "event.cta1", "event.cta2"] },
  { id: "about", label: "À Propos (Manifeste)", keys: ["about.eyebrow", "about.title.a", "about.title.b", "about.text", "about.story", "about.more", "about.less", "about.c1.t", "about.c1.x", "about.c2.t", "about.c2.x", "about.c3.t", "about.c3.x", "about.c4.t", "about.c4.x"] },
  { id: "services", label: "Services", keys: ["services.eyebrow", "services.title", "services.1.t", "services.1.x", "services.2.t", "services.2.x", "services.3.t", "services.3.x", "services.4.t", "services.4.x", "services.5.t", "services.5.x", "services.6.t", "services.6.x"] },
  { id: "players", label: "Talents", keys: ["players.eyebrow", "players.title", "players.text", "players.empty.t", "players.empty.x"] },
  { id: "staff", label: "Dirigeants & Employés", keys: ["staff.eyebrow", "staff.title.a", "staff.title.b", "staff.dir", "staff.emp", "staff.empty"] },
  { id: "reg", label: "Inscription Détection", keys: ["reg.eyebrow", "reg.title.a", "reg.title.b", "reg.text", "reg.b1", "reg.b2", "reg.b3", "reg.b4", "reg.b5", "reg.b6", "reg.quote", "reg.fee", "reg.terms.label", "reg.terms.link", "reg.terms.title", "reg.terms.body", "reg.closed.t", "reg.closed.x"] },
  { id: "detintro", label: "Présentation Détection", keys: ["det.intro.eyebrow", "det.intro.title.a", "det.intro.title.b", "det.intro.text", "det.intro.c1", "det.intro.c2", "det.intro.c3", "det.intro.c4", "det.tabs.label", "det.badge.open", "det.badge.closed", "det.badge.upcoming"] },
  { id: "teams", label: "Équipes D2R", keys: ["teams.eyebrow", "teams.title.a", "teams.title.b", "teams.text", "teams.empty", "teams.cta"] },
  { id: "lb", label: "Statistiques", keys: ["lb.eyebrow", "lb.title.a", "lb.title.b", "lb.text", "lb.scorers", "lb.assists", "lb.empty"] },
  { id: "eventpage", label: "Page Événement", keys: ["eventpage.phases.a", "eventpage.phases.b", "eventpage.phase1.tag", "eventpage.phase1.dates", "eventpage.phase1.i1", "eventpage.phase1.i2", "eventpage.phase1.i3", "eventpage.phase1.i4", "eventpage.phase1.i5", "eventpage.phase2.tag", "eventpage.phase2.dates", "eventpage.phase2.i1", "eventpage.phase2.i2", "eventpage.phase2.i3", "eventpage.phase2.i4", "eventpage.staff.title", "eventpage.staff.text", "eventpage.players.target", "eventpage.programme.full.a", "eventpage.programme.full.b", "eventpage.cta"] },
  { id: "news", label: "Actualités", keys: ["news.eyebrow", "news.title", "news.empty"] },
  { id: "contact", label: "Contact", keys: ["contact.eyebrow", "contact.title.a", "contact.title.b", "contact.text"] },
  { id: "footer", label: "Pied de page", keys: ["footer.text", "footer.agency", "footer.nav", "footer.follow", "footer.rights"] },
  { id: "pay", label: "Paiements & Bureau", keys: ["reg.pay.title", "reg.pay.fee", "reg.pay.card.t", "reg.pay.card.x", "reg.pay.mm.t", "reg.pay.mm.x", "reg.pay.office.t", "reg.pay.office.x", "pay.success.text", "pay.success.quote", "mm.s1", "mm.s2", "mm.s3", "office.text", "office.done.x"] },
];

function KeyEditor({ k, overrides, onSaved }) {
  const safeKey = k.replace(/\./g, "_");
  const ov = overrides[safeKey] || {};
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const hasOverride = Object.keys(ov).length > 0;

  const toggle = () => {
    if (!open) {
      const v = {};
      LANGS.forEach((l) => { v[l] = ov[l] ?? defaultTranslations[l]?.[k] ?? defaultTranslations.fr[k] ?? ""; });
      setValues(v);
    }
    setOpen(!open);
  };

  const save = async () => {
    setSaving(true);
    try {
      const translations = {};
      LANGS.forEach((l) => {
        if (values[l] !== (defaultTranslations[l]?.[k] ?? defaultTranslations.fr[k])) translations[l] = values[l];
      });
      await axios.put(`${API}/admin/content`, { key: k, translations }, { withCredentials: true });
      toast.success(`"${k}" mis à jour`);
      onSaved();
    } catch {
      toast.error("Erreur d'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    await axios.delete(`${API}/admin/content/${k}`, { withCredentials: true });
    toast.success("Texte d'origine restauré");
    onSaved();
  };

  const isLong = (defaultTranslations.fr[k] || "").length > 80;

  return (
    <div className="border border-[#27272A] bg-[#121214]" data-testid={`content-key-${safeKey}`}>
      <button onClick={toggle} className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#18181B] transition-colors" data-testid={`content-toggle-${safeKey}`}>
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-widest text-[#71717A]">{k}</p>
          <p className="text-sm text-[#A1A1AA] truncate mt-1">{ov.fr || defaultTranslations.fr[k]}</p>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {hasOverride && <span className="text-[9px] uppercase tracking-widest text-[#C5A059] border border-[#C5A059]/50 px-2 py-0.5">Modifié</span>}
          {open ? <ChevronUp size={15} className="text-[#71717A]" /> : <ChevronDown size={15} className="text-[#71717A]" />}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-[#27272A] pt-4 space-y-4">
          {LANGS.map((l) => (
            <div key={l}>
              <label className="text-[10px] uppercase tracking-widest text-[#C5A059]">{l.toUpperCase()}</label>
              {isLong ? (
                <textarea rows={4} value={values[l] || ""} onChange={(e) => setValues({ ...values, [l]: e.target.value })} data-testid={`content-input-${safeKey}-${l}`}
                  className="block w-full mt-1 bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]" />
              ) : (
                <input value={values[l] || ""} onChange={(e) => setValues({ ...values, [l]: e.target.value })} data-testid={`content-input-${safeKey}-${l}`}
                  className="block w-full mt-1 bg-[#0A0A0C] border border-[#27272A] px-3 py-2 text-sm focus:outline-none focus:border-[#C5A059]" />
              )}
            </div>
          ))}
          <div className="flex gap-3 pt-2">
            <button onClick={save} disabled={saving} data-testid={`content-save-${safeKey}`}
              className="flex items-center gap-2 bg-[#C5A059] text-black px-6 py-2.5 text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-50">
              <Save size={13} /> {saving ? "…" : "Enregistrer"}
            </button>
            {hasOverride && (
              <button onClick={reset} data-testid={`content-reset-${safeKey}`}
                className="flex items-center gap-2 border border-[#27272A] px-5 py-2.5 text-xs uppercase tracking-[0.2em] text-[#A1A1AA] hover:text-white transition-colors">
                <RotateCcw size={13} /> Restaurer
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default function AdminContent() {
  const [overrides, setOverrides] = useState({});
  const [openGroup, setOpenGroup] = useState("hero");

  const load = useCallback(() => {
    axios.get(`${API}/content`).then((r) => setOverrides(r.data.values || {})).catch(() => toast.error("Erreur de chargement du contenu"));
  }, []);
  useEffect(load, [load]);

  const modifiedCount = Object.keys(overrides).length;

  return (
    <div data-testid="admin-content">
      <p className="text-sm text-[#71717A] mb-8">
        Modifiez n'importe quel texte du site dans les 4 langues. Les changements sont visibles immédiatement sur le site public.
        {modifiedCount > 0 && <span className="text-[#C5A059]"> {modifiedCount} texte(s) personnalisé(s).</span>}
      </p>
      <div className="space-y-4">
        {GROUPS.map((g) => (
          <div key={g.id} className="border border-[#27272A]">
            <button onClick={() => setOpenGroup(openGroup === g.id ? null : g.id)} data-testid={`content-group-${g.id}`}
              className={`w-full flex items-center justify-between px-5 py-4 text-left transition-colors ${openGroup === g.id ? "bg-[#121214]" : "hover:bg-[#121214]"}`}>
              <span className="font-display uppercase font-medium text-sm tracking-wide">{g.label}</span>
              {openGroup === g.id ? <ChevronUp size={15} className="text-[#C5A059]" /> : <ChevronDown size={15} className="text-[#71717A]" />}
            </button>
            {openGroup === g.id && (
              <div className="p-4 space-y-3 border-t border-[#27272A]">
                {g.keys.map((k) => <KeyEditor key={k} k={k} overrides={overrides} onSaved={load} />)}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
